type ImageSource = {
  src: string;
  width: number;
  height: number;
};

export type ResponsiveImageEntry = {
  imageId: string;
  desktopSrc: string;
  mobileSrc: string;
  sources: {
    desktop: {
      webp: readonly ImageSource[];
      avif: readonly ImageSource[];
    };
    mobile: {
      webp: readonly ImageSource[];
      avif: readonly ImageSource[];
    };
  };
  alt: string;
  focalPoint: {
    desktop: { x: number; y: number };
    mobile: { x: number; y: number };
  };
  loading: "eager" | "lazy";
  fetchPriority: "high" | "low" | "auto";
  sizes: string;
};

type ResponsiveImageProps = {
  image: ResponsiveImageEntry;
  className?: string;
  mobilePortrait?: boolean;
  signature?: string;
};

function toSrcSet(sources: readonly ImageSource[]) {
  return sources.map(({ src, width }) => `${src} ${width}w`).join(", ");
}

export function ResponsiveImage({
  image,
  className = "",
  mobilePortrait = false,
  signature,
}: ResponsiveImageProps) {
  const fallback =
    image.sources.desktop.webp.at(-1) ??
    image.sources.desktop.avif.at(-1) ??
    image.sources.mobile.webp.at(-1) ??
    image.sources.mobile.avif.at(-1);
  const pictureClassName = [
    "responsive-image",
    mobilePortrait ? "responsive-image--mobile-portrait" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <picture
      className={pictureClassName}
      data-asset-id={image.imageId}
      data-auth-signature={signature}
      data-desktop-focal={`${image.focalPoint.desktop.x},${image.focalPoint.desktop.y}`}
      data-mobile-focal={`${image.focalPoint.mobile.x},${image.focalPoint.mobile.y}`}
    >
      <source
        media="(max-width: 767px)"
        type="image/avif"
        srcSet={toSrcSet(image.sources.mobile.avif)}
        sizes={image.sizes}
      />
      <source
        media="(max-width: 767px)"
        type="image/webp"
        srcSet={toSrcSet(image.sources.mobile.webp)}
        sizes={image.sizes}
      />
      <source
        media="(min-width: 768px)"
        type="image/avif"
        srcSet={toSrcSet(image.sources.desktop.avif)}
        sizes={image.sizes}
      />
      <source
        media="(min-width: 768px)"
        type="image/webp"
        srcSet={toSrcSet(image.sources.desktop.webp)}
        sizes={image.sizes}
      />
      <img
        src={image.desktopSrc || image.mobileSrc}
        alt={image.alt}
        width={fallback?.width ?? 1280}
        height={fallback?.height ?? 960}
        loading={image.loading}
        fetchPriority={image.fetchPriority}
        decoding="async"
        data-image-id={image.imageId}
      />
    </picture>
  );
}
